import React, { useState } from 'react';
import { Button, CloseButton, Modal, Spinner } from 'react-bootstrap';

const TITLE_ID = 'reject-student-application-title';
const DESCRIPTION_ID = 'reject-student-application-description';

function consequenceText({ takesBackOffer, programLabel, term, offered }) {
    if (takesBackOffer) {
        const subject = offered ? `The offer of ${offered}` : 'The offer';
        return `${subject} is withdrawn and the application is closed as rejected. The student gets a notice that the offer was taken back. This can't be undone from the site.`;
    }
    return `A rejection is final: it can't be undone from the site, and the student can't apply to the ${programLabel} program again for ${term}. The student sees "Rejected" on their applications; no notice is sent.`;
}

/**
 * Asks a coordinator to confirm Reject, or Take back offer when the student
 * holds an open offer, before anything is recorded. A rejection is final.
 * `onConfirm` records it and answers true when it was recorded; a refusal is
 * the page's to show, so this closes by calling `onCancel` on false.
 */
export default function RejectStudentApplicationModal({
    show,
    takesBackOffer,
    studentName,
    programLabel,
    term,
    offeredCourseLabel,
    onConfirm,
    onCancel,
}) {
    const [submitting, setSubmitting] = useState(false);

    const close = () => {
        if (submitting) return;
        onCancel();
    };

    const confirm = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const recorded = await onConfirm();
            if (recorded === false) onCancel();
        } finally {
            setSubmitting(false);
        }
    };

    const confirmLabel = takesBackOffer
        ? 'Take back offer'
        : 'Reject application';

    return (
        <Modal
            show={show}
            onHide={close}
            aria-labelledby={TITLE_ID}
            aria-describedby={DESCRIPTION_ID}
        >
            <Modal.Header>
                <Modal.Title id={TITLE_ID} as="h2" className="h5">
                    {takesBackOffer
                        ? 'Take back this offer?'
                        : 'Reject this application?'}
                </Modal.Title>
                <CloseButton
                    aria-label="Close"
                    onClick={close}
                    disabled={submitting}
                />
            </Modal.Header>
            <Modal.Body>
                <div id={DESCRIPTION_ID}>
                    <dl className="row mb-3">
                        <dt className="col-sm-4">Student</dt>
                        <dd className="col-sm-8">
                            {studentName || 'Not provided'}
                        </dd>
                        <dt className="col-sm-4">Program</dt>
                        <dd className="col-sm-8">{programLabel}</dd>
                        <dt className="col-sm-4">Term</dt>
                        <dd className="col-sm-8">{term}</dd>
                        {takesBackOffer && (
                            <>
                                <dt className="col-sm-4">Offered course</dt>
                                <dd className="col-sm-8">
                                    {offeredCourseLabel || 'Not recorded'}
                                </dd>
                            </>
                        )}
                    </dl>
                    <p className="mb-0">
                        {consequenceText({
                            takesBackOffer,
                            programLabel,
                            term,
                            offered: offeredCourseLabel,
                        })}
                    </p>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={close}
                    disabled={submitting}
                >
                    Go back
                </Button>
                <Button
                    variant="danger"
                    onClick={confirm}
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                                aria-hidden="true"
                            />
                            Saving...
                        </>
                    ) : (
                        confirmLabel
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
